import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { TreeNode } from "./build-tree";

interface FileTreeProps {
  nodes: TreeNode[];
  selectedNodeId: string | null;
  onSelectFile: (node: TreeNode) => void;
}

export function FileTree({
  nodes,
  selectedNodeId,
  onSelectFile,
}: FileTreeProps) {
  return (
    <ul className="space-y-0.5">
      {nodes.map((node) => (
        <FileTreeNode
          key={node.id}
          node={node}
          depth={0}
          selectedNodeId={selectedNodeId}
          onSelectFile={onSelectFile}
        />
      ))}
    </ul>
  );
}

interface FileTreeNodeProps {
  node: TreeNode;
  depth: number;
  selectedNodeId: string | null;
  onSelectFile: (node: TreeNode) => void;
}

function FileTreeNode({
  node,
  depth,
  selectedNodeId,
  onSelectFile,
}: FileTreeNodeProps) {
  const [expanded, setExpanded] = useState(depth < 1);
  const isFolder = node.type === "directory";
  const isSelected = !isFolder && node.id === selectedNodeId;

  const handleClick = () => {
    if (isFolder) {
      setExpanded((prev) => !prev);
    } else {
      onSelectFile(node);
    }
  };

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        style={{ paddingLeft: depth * 14 + 8 }}
        className={cn(
          "flex w-full items-center gap-1.5 rounded-md py-1.5 pr-2 text-left text-sm transition-colors hover:bg-accent",
          isSelected && "bg-accent font-medium"
        )}
      >
        {isFolder ? (
          <>
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            {expanded ? (
              <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </>
        ) : (
          <FileText className="ml-[14px] h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate">{node.name || "제목 없음"}</span>
      </button>

      {isFolder && expanded && node.children.length > 0 && (
        <ul className="space-y-0.5">
          {node.children.map((child) => (
            <FileTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedNodeId={selectedNodeId}
              onSelectFile={onSelectFile}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
